import React, { useState } from "react";
import { Spin, Button, Input, Modal, message, Form, Divider } from "antd";
import {
  UserOutlined,
  WalletOutlined,
  GiftOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import { BillingInformationForm } from "./BillingInformationForm";
import { Subscription } from "./Subscription";
import { userStore } from "../../stores/UserStore";
import { observer } from "mobx-react";
import { SessionOverview } from "./SessionOverview";
import { OrderOverview } from "./OrderOverview";
import Api from "../../api";
import { showError } from "../../utils";
import { StripeInvoiceList } from "./StripeInvoices";
import { ReferralCodeDisplay } from "../../Components/ReferralCodeDisplay";
import { SectionCard } from "../../Components/SectionCard";

export const Account = observer(() => {
  const { userInfo } = userStore;

  if (!userInfo)
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: 300,
        }}
      >
        <Spin size="large" />
      </div>
    );

  const isCreditsUser = userInfo.licenseType === "credits";
  const credits = Number(userInfo.credits) || 0;
  const formattedCredits = credits.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
  const planLabel = isCreditsUser ? "Pay-as-you-go" : "Monthly License";
  const initial = (userInfo.email || "?").charAt(0).toUpperCase();

  const renderProfileCard = () => (
    <SectionCard
      icon={<UserOutlined />}
      title="Profile"
      description="Your account details and sign-in"
      actions={<ChangePassword userId={userInfo.user_id} />}
      className="profile-card"
    >
      <div className="profile-card-content">
        <div className="profile-avatar" aria-hidden="true">
          {initial}
        </div>
        <div className="profile-info">
          <div className="profile-email" title={userInfo.email}>
            {userInfo.email}
          </div>
          <div className="profile-meta">
            <span className={`plan-badge plan-badge--${userInfo.licenseType}`}>
              {planLabel}
            </span>
          </div>
        </div>
      </div>
    </SectionCard>
  );

  const renderCreditsCard = () => {
    if (!isCreditsUser) return null;
    return (
      <SectionCard
        icon={<WalletOutlined />}
        title="Credits"
        actions={
          <a href="/credits">
            <Button type="primary" size="large" className="buy-credits-button">
              Buy Credits
            </Button>
          </a>
        }
        className="credits-card"
      >
        <div className="credits-card-content">
          <div className="big-stat">{formattedCredits}</div>
          <div className="credits-card-caption">credits available</div>
        </div>
      </SectionCard>
    );
  };

  return (
    <div className="account-page">
      <div className="account-header">
        <h1>Account</h1>
        <p>Manage your subscription, billing, and history</p>
      </div>

      <div
        className={`account-hero${
          isCreditsUser ? " account-hero--two" : " account-hero--one"
        }`}
      >
        {renderProfileCard()}
        {renderCreditsCard()}
      </div>

      <Subscription />

      <SectionCard
        icon={<GiftOutlined />}
        title="Refer & Earn"
        description="Share your code with new Hero Zone operators and earn credits!"
        actions={
          <a href="/affiliate">
            <Button>Affiliate stats</Button>
          </a>
        }
      >
        <ReferralCodeDisplay userId={userInfo.user_id} />
      </SectionCard>

      {/* <SectionCard
        icon={<PictureOutlined />}
        title="Marketing Materials"
        description="Download posters, trailers, and other promotional assets"
        actions={
          <a
            href="https://drive.google.com/drive/folders/1S2q2Cd_-wMGqYf0Vi_ySBzbcmAj26NRM?usp=sharing"
            rel="noopener noreferrer"
            target="_blank"
          >
            <Button>Open Drive Folder</Button>
          </a>
        }
      /> */}

      <SectionCard
        icon={<IdcardOutlined />}
        title="Billing Information"
        description="Company details and address used for your invoices"
      >
        <BillingInformationForm />
      </SectionCard>

      <SessionOverview />
      <StripeInvoiceList />
      <OrderOverview />
    </div>
  );
});

const ChangePassword = (props: { userId: number }) => {
  const [showModal, setShowModal] = useState(false);
  const [pendingChange, setPendingChange] = useState(false);
  const onFinish = async (values: any) => {
    setPendingChange(true);
    try {
      await Api.put(`/api/users/${props.userId}/password`, {
        newPassword: values.newPassword,
      });
      message.success("Password changed successfully");
    } catch (error) {
      showError("failed to change password", error);
    }
    setPendingChange(false);
    setShowModal(false);
  };
  return (
    <>
      <Button onClick={() => setShowModal(true)}>Change Password</Button>
      <Modal
        visible={showModal}
        title="Change Password"
        closable
        onCancel={() => setShowModal(false)}
        footer={null}
      >
        <Form onFinish={onFinish}>
          <Form.Item
            label="New Password"
            name="newPassword"
            rules={[
              {
                required: true,
                message: "Please enter a new password",
              },
              {
                min: 6,
                message: "Password must be at least 6 characters",
              },
            ]}
          >
            <Input type={"password"} placeholder="Enter new password..." />
          </Form.Item>
          <Divider />
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              style={{ marginRight: 6 }}
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button loading={pendingChange} type="primary" htmlType="submit">
              Confirm
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};
